/**
 * The unsigned-pilot update notice and the latest-release summary (epic
 * PI-SPEC-SW-E288, PKG-PM-H "Getting started", 03-evaluation.md section 8.6
 * Q3).
 *
 * Until the Toolbox is code-signed it cannot update itself, so a person
 * downloads each new version from SpecTree's download page and reinstalls it.
 * Because the bundled SpecTree MCP server and the skills ship inside the app
 * (section 8.6 Q1, Q2b), that notice is the only way fixes reach them: the
 * app reports it through the optional `ToolboxBridge.app.getUpdateNotice`,
 * and the page shows a band that is hard to miss.
 *
 * - `none`: the app is current;
 * - `available`: a newer version can be downloaded;
 * - `required`: this app is too old to keep working with this SpecTree (the
 *   contract says `app-update-needed`, or the bundled MCP server is older than
 *   SpecTree's `toolboxBundledMcp.min`). `pausedReason` says, in plain words,
 *   what is paused until the person updates.
 *
 * No credential and no service URL here: the download page is a SpecTree path.
 * Re-exported from `./contracts`, so Node code (the SpecTree API) can import
 * it without the bridge or the `window` global.
 */
import { z } from "zod";
/** Path of the latest-release summary the download page and the app read (sign-in required). */
export const TOOLBOX_LATEST_PATH = "/api/v1/toolbox/latest";
/** SpecTree page where a person downloads the Toolbox (sign-in required). */
export const TOOLBOX_DOWNLOAD_PAGE_PATH = "/toolbox/download";
/** The three states of the in-app update band. */
export const TOOLBOX_UPDATE_NOTICE_STATES = ["none", "available", "required"];
/** Longest one-sentence release note or paused reason shown in the band. */
export const TOOLBOX_UPDATE_NOTE_MAX = 240;
/**
 * What `ToolboxBridge.app.getUpdateNotice()` resolves to and
 * `onUpdateNotice` delivers. `notes` is one plain sentence ("It fixes how
 * Claude's runs save briefs."). Unknown keys are stripped.
 */
export const toolboxUpdateNoticeSchema = z.object({
    state: z.enum(TOOLBOX_UPDATE_NOTICE_STATES),
    /** The running app's version. */
    currentVersion: z.string().min(1),
    /** The newest version on the download page; absent when unknown. */
    latestVersion: z.string().min(1).optional(),
    notes: z.string().max(TOOLBOX_UPDATE_NOTE_MAX).optional(),
    /** For `required`: what is paused until the person updates, in plain words. */
    pausedReason: z.string().max(TOOLBOX_UPDATE_NOTE_MAX).optional(),
});
/** One platform's installer on the download page. */
export const toolboxReleaseFileSchema = z.object({
    /** The app version, for example "0.5.0". */
    version: z.string().min(1),
    /** The installer's file name in the update feed, for example "SpecTree Toolbox Setup 0.5.0.exe". */
    fileName: z.string().min(1).max(200),
    sizeBytes: z.number().int().min(0),
    /** When it was released, ISO 8601. */
    releaseDate: z.string().min(1),
});
/**
 * The `data` of `GET /api/v1/toolbox/latest`: the newest installer per
 * platform, read from the update feed. A platform with no published build is
 * absent. Unknown keys (a future `linux`) are stripped.
 */
export const toolboxLatestReleaseSchema = z.object({
    windows: toolboxReleaseFileSchema.optional(),
    mac: toolboxReleaseFileSchema.optional(),
    /** One plain sentence about the newest release. */
    notes: z.string().max(TOOLBOX_UPDATE_NOTE_MAX).optional(),
});
//# sourceMappingURL=updates.js.map